import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Pagination,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatDateTimeMonth } from "../../../../../../../../../utils/functions";
import { AccountCircleOutlined, Update, ExpandMore } from "@mui/icons-material";
import {
  Exception,
  Exceptions,
} from "../../../../../../../../../api/homeSiteExceptions/types";
import UpdateExceptionForm from "./UpdateExceptionForm";
import { Note } from "../../../../../../../../../api/homeSiteNotes/types";

interface ExceptionListProps {
  exceptions: Exceptions;
  page: number;
  itemsPerPage: number;
  updateException: (exception: Partial<Exception>) => void;
  createNote: any;
  updateNote: (note: Partial<Note>) => void;
  setActivePage: (page: number) => void;
  homeSiteId: string;
  userId: string;
  title: string;
  notes: Record<string, Note[]>;
  showResolved: boolean;
  divisionCode: string;
  conciergeEmail: string;
  showUpdateForm: boolean;
  setShowUpdateForm: (value: boolean) => void;
  opportunityId: string;
  setExceptionsDirty: (dirty: boolean) => void;
  setExceptionUpdateDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  setExceptionUpdateHandler?: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const ExceptionList: React.FC<ExceptionListProps> = ({
  exceptions,
  page,
  itemsPerPage,
  updateException,
  createNote,
  updateNote,
  setActivePage,
  homeSiteId,
  userId,
  title,
  notes,
  showResolved,
  divisionCode,
  conciergeEmail,
  showUpdateForm,
  setShowUpdateForm,
  opportunityId,
  setExceptionsDirty,
  setExceptionUpdateHandler,
  setExceptionUpdateDirty,
  showSnackbar,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedException, setSelectedException] = useState<Exception | null>(
    null
  );
  const [isResolveMode, setIsResolveMode] = useState(false); // Track resolve mode

  /* Sort exceptions by updated date if showResolved is true */
  const sortedExceptions = showResolved
    ? [...exceptions.data].sort(
        (a, b) =>
          new Date(b.updatedOnDate).getTime() -
          new Date(a.updatedOnDate).getTime()
      )
    : exceptions.data;

  const totalPages = Math.ceil(sortedExceptions.length / itemsPerPage);

  useEffect(() => {
    if (page < 1 && totalPages > 0) {
      /* In an empty list, as soon as an exception is added, the page is set to be 1 */
      setActivePage(1);
    } else if (page > totalPages) {
      /* If there are 6 exceptions and 1 gets resolved, page 2 goes away and we need to move back to page 1 */
      setActivePage(totalPages);
    }
  }, [page, totalPages, setActivePage]);

  /*
    Check commit notes for understanding logic - git log <commit id = 505388a4b332af3146fbc687b4e84ef3c3efc286>
  */
  const exceptionsToDisplay = useMemo(
    () =>
      sortedExceptions.slice((page - 1) * itemsPerPage, page * itemsPerPage),
    [sortedExceptions, page, itemsPerPage]
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    exception: Exception
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedException(exception);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleResolveClick = (exception: Exception) => {
    setSelectedException(exception);
    setIsResolveMode(true); // Enable resolve mode
    setShowUpdateForm(true); // Show the update form
    handleMenuClose();
  };

  const handleSaveUpdate = async (updateComment: string) => {
    if (!selectedException) return;
    const noteData: Partial<Note> = {
      description: updateComment,
      homeSiteId,
      mileStoneName: selectedException.mileStoneName,
      divisionCode: divisionCode,
      conciergeEmail: conciergeEmail,
      noteType: "exceptionNotes",
      exceptionId: selectedException.id,
      createdBy: userId,
      opportunityId,
    };
    const note = await createNote(noteData);

    if (isResolveMode) {
      await updateException({
        id: selectedException.id,
        homeSiteId,
        divisionCode: divisionCode,
        status: false, // Mark exception as resolved
        updatedBy: userId,
      });

      await updateNote({
        id: note.id,
        homeSiteId: note.homeSiteId,
        divisionCode: divisionCode,
        mileStoneName: note.mileStoneName,
        status: false,
        updatedBy: userId,
        opportunityId,
      });
      showSnackbar("Exception resolved successfully!", "success");
    } else {
      showSnackbar("Exception updated successfully!", "success");
    }

    setShowUpdateForm(false);
    setIsResolveMode(false);
  };

  const handleUpdateClick = (exception: Exception) => {
    setSelectedException(exception);
    setShowUpdateForm(true);
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        overflow: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none", // IE and Edge
        "scrollbar-width": "none", // Firefox
      }}
    >
      {!showUpdateForm ? (
        <>
          {exceptionsToDisplay.map((exception: Exception) => (
            <Card
              key={exception.id}
              variant="outlined"
              style={{
                marginBottom: "8px",
                backgroundColor: exception.status ? "#FFEFF1" : "#FFFFFF",
                border: exception.status ? "default" : "1.5px solid #C93939",
                position: "relative",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    backgroundColor: "#C93939",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "12px",
                    padding: "4px 12px",
                    borderRadius: "16px",
                    display: "inline-block",
                    marginBottom: "8px",
                  }}
                >
                  {exception.mileStoneName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  {exception.exceptionName}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    marginBottom: "8px",
                    fontSize: "14px",
                    wordWrap: "break-word",
                  }}
                >
                  {exception.description}
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  marginTop="8px"
                >
                  <Box display="flex" alignItems="center">
                    <AccountCircleOutlined />
                    <Typography variant="caption" marginLeft="8px">
                      {exception.createdBy}
                    </Typography>
                  </Box>
                  <Typography variant="caption">
                    {formatDateTimeMonth(exception.createdOnDate)}
                  </Typography>
                </Box>
                {exception.status ? (
                  <IconButton
                    onClick={(event) => handleMenuOpen(event, exception)}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "4px",
                      color: "#1C2B36",
                    }}
                  >
                    <Tooltip title="Menu" arrow placement="left">
                      <MoreVertIcon />
                    </Tooltip>
                  </IconButton>
                ) : (
                  <IconButton
                    disabled
                    style={{ position: "absolute", top: "8px", right: "4px" }}
                  >
                    <CheckCircleIcon sx={{ color: "#FF9A9A" }} />
                  </IconButton>
                )}
              </CardContent>

              {/* Additional Updates Accordion */}
              {notes[exception.id]?.filter(
                (note) => note.status && note.noteType === "exceptionNotes"
              ).length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    sx={{ backgroundColor: "#FFEFF1" }}
                    expandIcon={<ExpandMore />}
                    aria-controls={`panel-${exception.id}-content`}
                    id={`panel-${exception.id}-header`}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#1C2B36",
                        fontWeight: "700",
                        fontSize: "12px",
                      }}
                    >
                      Additional Updates
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ backgroundColor: "#FFEFF1", top: "0px" }}
                  >
                    {notes[exception.id]
                      .filter(
                        (note) =>
                          note.status && note.noteType === "exceptionNotes"
                      )
                      .map((note, index) => (
                        <Box key={note.id || index} marginBottom={2}>
                          <Typography variant="body2">
                            {note.description}
                          </Typography>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            marginTop="4px"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <AccountCircleOutlined />
                              <Typography variant="caption">
                                {note.createdBy}
                              </Typography>
                            </Box>
                            <Typography variant="caption">
                              {formatDateTimeMonth(note.createdOnDate)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </AccordionDetails>
                </Accordion>
              )}
              {showResolved && notes[exception.id]?.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    sx={{ backgroundColor: "#FFEFF1" }}
                    expandIcon={<ExpandMore />}
                    aria-controls={`panel-${exception.id}-resolve-content`}
                    id={`panel-${exception.id}-resolve-header`}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#1C2B36",
                        fontWeight: "700",
                        fontSize: "12px",
                      }}
                    >
                      Resolve Comment
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ backgroundColor: "#FFEFF1", top: "0px" }}
                  >
                    {notes[exception.id]
                      .filter(
                        (note) =>
                          !note.status && note.noteType === "exceptionNotes"
                      )
                      .map((note, index) => (
                        <Box key={note.id || index} marginBottom={2}>
                          <Typography variant="body2">
                            {note.description}
                          </Typography>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            marginTop="4px"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <AccountCircleOutlined />
                              <Typography variant="caption">
                                {note.createdBy}
                              </Typography>
                            </Box>
                            <Typography variant="caption">
                              {formatDateTimeMonth(note.createdOnDate)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Card>
          ))}
          {exceptions.data.length > itemsPerPage && (
            <Box marginTop={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setActivePage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <UpdateExceptionForm
          key={selectedException?.id} // Add a unique key
          exception={selectedException}
          notes={notes}
          handleSaveUpdate={handleSaveUpdate}
          title={isResolveMode ? "Resolve Exception" : "Update Exception"}
          handleBack={() => {
            setShowUpdateForm(false);
            setIsResolveMode(false);
          }}
          onExceptionDirtyChange={(dirty) => {
            setExceptionsDirty(dirty);
            setExceptionUpdateDirty?.(dirty);
          }}
          onSetExceptionUpdateHandler={(fn) => {
            setExceptionUpdateHandler?.(() => fn);
          }}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <MuiMenuItem onClick={() => handleUpdateClick(selectedException!)}>
          <ListItemIcon>
            <Update sx={{ color: "green" }} />
          </ListItemIcon>
          Update
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleResolveClick(selectedException!)}>
          <ListItemIcon>
            <CheckCircleIcon sx={{ color: "green" }} />
          </ListItemIcon>
          Resolve
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};

export default ExceptionList;
